import React from "react";
import {Button, Tooltip} from "antd";
import {embed, unembed} from "../Prompt";
import {useParams} from "react-router-dom";
import {useAppDispatch, useAppSelector} from "../../feature/tools";
import {openAccountSettings, openProjectSettings} from "../../feature/project/appSettingsSlice";

export class EmbeddingData {
    id: string = ''
    text: string = ''
    metadata?: Record<string, string>
}

export const EmbedButton = (
    props: {
        data: EmbeddingData | EmbeddingData[],
        embedded?: boolean,
        edited?: boolean,
        style?: React.CSSProperties,
        disabled?: boolean,
        chunkSize?: number,
        chunkOverlap?: number,
        onEmbedded?: (data: EmbeddingData) => void,
        onUnembedded?: (data: EmbeddingData | EmbeddingData[]) => void,
    }
) => {
    const dispatch = useAppDispatch()
    const {projectId, storyId} = useParams()
    const [loading, setLoading] = React.useState(false)
    const aiProviders = useAppSelector(state => state.aiSlice.aiProviderSettings)
    const projectAiProviders = useAppSelector(state => state.aiSlice.projectAiProviderSettings)
    const [openAiEnabled, setOpenAiEnabled] = React.useState(false)
    const [openAiSelected, setOpenAiSelected] = React.useState(false)
    const user = useAppSelector(state => state.users.currentUser)

    React.useEffect(() => {
        const openAiSelected = projectAiProviders.filter(p => p.enabled).map(p => p.name).includes('OpenAI')
        const openAiEnabled = aiProviders.filter(p => p.enabled).map(p => p.name).includes('OpenAI')
        setOpenAiEnabled(openAiEnabled || user?.role != 'creator')
        setOpenAiSelected(openAiSelected)
    }, [projectAiProviders, aiProviders])

    const sufix = Array.isArray(props.data) && props.data.length > 0 ? ' All' : ''

    return (
        <Tooltip
            title={!openAiEnabled || !openAiSelected ? <>To use embedding functionality you need to:
                {!openAiEnabled ? <><br/>- enable OpenAI provider in the<br/>
                    <Button
                        size={'small'}
                        onClick={() => dispatch(openAccountSettings('ai'))}
                    >Account Settings
                    </Button></> : ''}
                {!openAiSelected ? <><br/>- select OpenAI provider in the<br/>
                    <Button size={'small'}
                            onClick={() => dispatch(openProjectSettings('ai'))}
                    >
                        Project Settings
                    </Button></> : ''}
            </> : undefined}>
            <Button
                type={'primary'}
                disabled={!openAiEnabled || !openAiSelected}
                loading={loading}
                danger={props.embedded && !props.edited}
                onClick={() => {
                    setLoading(true)
                    const embeds = Array.isArray(props.data) ? props.data : [props.data]
                    if (props.embedded == false) {
                        embeds.forEach((data) => {
                            embed({
                                ...data,
                                metadata: {...data.metadata, storyId: storyId ?? ""},
                                project: projectId,
                                chunkSize: props.chunkSize ?? 800,
                                chunkOverlap: props.chunkOverlap ?? 0
                            })
                                .then(() => {
                                    props.onEmbedded?.(data)
                                })
                                .catch((e) => {
                                    console.log(e)
                                })
                                .finally(() => {
                                    setLoading(false)
                                })
                        })
                    } else if (props.embedded == true) {
                        unembed({project: projectId, ids: embeds.map(d => d.id)})
                            .then((data) => {
                                props.onUnembedded?.(props.data)
                            })
                            .catch((e) => {
                                console.log(e)
                            })
                            .finally(() => {
                                setLoading(false)
                            })
                    } else {
                        unembed({project: projectId, ids: embeds.map(d => d.id)})
                            .then((data) => {
                                return embeds.forEach((data) => {
                                    embed({
                                        ...data,
                                        project: projectId,
                                        chunkSize: props.chunkSize ?? 800,
                                        chunkOverlap: props.chunkOverlap ?? 0
                                    })
                                        .then(() => {
                                            props.onEmbedded?.(data)
                                        })
                                        .catch((e) => {
                                            console.log(e)
                                        })
                                        .finally(() => {
                                            setLoading(false)
                                        })
                                })
                            })
                            .catch((e) => {
                                console.log(e)
                            })
                    }
                }}>
                {props.embedded == true ? `Un-Embed${sufix}` : props.embedded == undefined ? `Re-Embed${sufix}` : `Embed${sufix}`}
            </Button>
        </Tooltip>
    )
}